const TECHNICIAN_MAP_ALLOWED_USER_IDS = [339, 348, 383, 385, 334, 333, 220];

export const canAccessTechnicianMap = (user = {}) => {
    const userId = parseInt(user.id, 10);
    return TECHNICIAN_MAP_ALLOWED_USER_IDS.includes(userId);
};

export const STATUS_META = {
    moving: { label: 'Em movimento', short: 'Movimento', color: '#2E7D32' },
    stopped: { label: 'Parado', short: 'Parado', color: '#ED6C02' },
    alert: { label: 'Alerta', short: 'Alerta', color: '#C62828' },
    offline: { label: 'Offline', short: 'Offline', color: '#78909C' }
};

export const splitTeams = (equipe) => {
    const unique = [];
    const seen = new Set();
    String(equipe || '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
        .forEach((name) => {
            const key = name.toLowerCase();
            if (seen.has(key)) return;
            seen.add(key);
            unique.push(name);
        });
    return unique;
};

export const haversineMeters = (lat1, lng1, lat2, lng2) => {
    const toRad = (value) => (value * Math.PI) / 180;
    const earth = 6371000;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return 2 * earth * Math.asin(Math.min(1, Math.sqrt(a)));
};

const parseTrackTime = (iso) => {
    if (!iso) return 0;
    const value = new Date(iso).getTime();
    return Number.isNaN(value) ? 0 : value;
};

const toLatLng = (item) => {
    const lat = Number(item?.lat ?? item?.latitude);
    const lng = Number(item?.lng ?? item?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
    return {
        lat,
        lng,
        at: parseTrackTime(item?.position_at)
    };
};

const dropGpsSpikes = (points) => {
    if (points.length < 3) return points;
    return points.filter((curr, i, list) => {
        if (i === 0 || i === list.length - 1) return true;
        const prev = list[i - 1];
        const next = list[i + 1];
        const dPrev = haversineMeters(prev.lat, prev.lng, curr.lat, curr.lng);
        const dSkip = haversineMeters(prev.lat, prev.lng, next.lat, next.lng);
        const dt = Math.max(1, (curr.at - prev.at) / 1000);
        const teleport = dPrev > Math.max(250, dt * 50 * 1.2);
        const comesBack = dSkip < Math.min(dPrev * 0.45, dPrev - 80);
        return !(teleport && comesBack);
    });
};

export const trackSegments = (items = []) => {
    const sorted = [...items]
        .map(toLatLng)
        .filter(Boolean)
        .sort((a, b) => a.at - b.at || 0);
    const cleaned = dropGpsSpikes(sorted);
    const segments = [];
    let current = [];
    let last = null;
    cleaned.forEach((point) => {
        const next = [point.lat, point.lng];
        if (last) {
            const meters = haversineMeters(last.lat, last.lng, point.lat, point.lng);
            const dt = Math.max(0, (point.at - last.at) / 1000);
            if (meters < 25) {
                last = point;
                if (current.length) current[current.length - 1] = next;
                return;
            }
            const impliedKmh = dt > 0 ? (meters / dt) * 3.6 : 0;
            const teleport = dt > 0 && meters > 400 && impliedKmh > 160;
            if (teleport) {
                if (current.length >= 2) segments.push(current);
                current = [next];
                last = point;
                return;
            }
        }
        current.push(next);
        last = point;
    });
    if (current.length >= 2) {
        segments.push(current);
    } else if (current.length === 1 && segments.length === 0) {
        segments.push(current);
    }
    return segments;
};

export const formatHourMinute = (iso) => {
    if (!iso) return '—';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

export const formatClock = (iso) => {
    if (!iso) return '—';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

export const todayYmd = () => {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${now.getFullYear()}-${month}-${day}`;
};

export const formatMinutes = (minutes) => {
    if (minutes === null || minutes === undefined) return '—';
    const value = Number(minutes);
    if (Number.isNaN(value)) return '—';
    if (value < 60) return `${value} min`;
    const hours = Math.floor(value / 60);
    const rest = value % 60;
    return `${hours}h ${String(rest).padStart(2, '0')}min`;
};

export const formatSpeed = (speed) => {
    if (speed === null || speed === undefined || Number.isNaN(Number(speed))) return '—';
    return `${Number(speed).toLocaleString('pt-BR', { maximumFractionDigits: 0 })} km/h`;
};

export const isAtDepot = (lat, lng, depot) => {
    if (lat == null || lng == null || !depot) return false;
    const depotLat = Number(depot.latitude);
    const depotLng = Number(depot.longitude);
    const radius = Number(depot.radius_meters || 500);
    if (!Number.isFinite(depotLat) || !Number.isFinite(depotLng)) return false;
    if (depotLat === 0 && depotLng === 0) return false;
    return haversineMeters(Number(lat), Number(lng), depotLat, depotLng) <= radius;
};

export const nearestUnit = (point, units = []) => {
    if (!point || point.lat == null || point.lng == null || !units.length) return null;
    let best = null;
    let bestDistance = null;
    units.forEach((unit) => {
        const distance = haversineMeters(point.lat, point.lng, unit.latitude, unit.longitude);
        if (bestDistance === null || distance < bestDistance) {
            bestDistance = distance;
            best = unit;
        }
    });
    if (!best) return null;
    return { id: best.id, nome: best.nome, distance_meters: Math.round(bestDistance) };
};

export const formatAlertSummary = (tech) => {
    const idle = tech?.alert?.idle_minutes ?? tech?.idle_minutes;
    const unit = tech?.alert?.nearest_unit || tech?.nearest_unit?.nome;
    if (idle != null && idle !== '') {
        return unit ? `Parado ${idle} min · ${unit}` : `Parado ${idle} min fora da unidade`;
    }
    const message = String(tech?.alert?.message || 'Parado fora do raio da unidade');
    const name = String(tech?.nome || '').trim();
    if (name && message.toLowerCase().startsWith(name.toLowerCase())) {
        return message.slice(name.length).replace(/^[,\s]+/, '').replace(/^p/, 'P');
    }
    return message;
};
